import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, 
  TrendingDown, 
  Settings, 
  RefreshCw, 
  Search, 
  Filter, 
  ChevronDown, 
  Sliders, 
  Clock, 
  Zap 
} from 'lucide-react';
import { 
  DetectedChange, 
  MovementDataPoint, 
  MovementMetric, 
  MovementMetricType, 
  AnalysisConfiguration, 
  SubtleChangeNotificationSettings 
} from './interfaces/types';
import { 
  generateMovementData, 
  getMovementMetrics 
} from './utils/MovementDataSimulator';
import { 
  detectSubtleChanges, 
  defaultAnalysisConfig 
} from './utils/PatternAnalysisEngine';
import ChangeDetailCard from './ChangeDetailCard';
import SettingsPanel from './SettingsPanel';
import TrendVisualizer from './TrendVisualizer';
import { calculateTrend } from './utils/PatternAnalysisEngine';

const SubtleChangeDetection: React.FC = () => {
  // State for movement data and metrics
  const [movementData, setMovementData] = useState<MovementDataPoint[]>([]);
  const [metrics, setMetrics] = useState<MovementMetric[]>(getMovementMetrics());
  
  // State for detected changes
  const [detectedChanges, setDetectedChanges] = useState<DetectedChange[]>([]);
  
  // State for configuration
  const [analysisConfig, setAnalysisConfig] = useState<AnalysisConfiguration>({...defaultAnalysisConfig});
  const [notificationSettings, setNotificationSettings] = useState<SubtleChangeNotificationSettings>({
    notificationsEnabled: true,
    caregiversToNotify: ['Dr. Sarah Johnson'],
    minSignificanceToNotify: 'MODERATE',
    notificationFrequency: 'DAILY',
    includeRecommendations: true,
    autoGenerateReports: false
  });
  
  // UI state
  const [activeTab, setActiveTab] = useState<'detected' | 'trends' | 'settings'>('detected');
  const [loading, setLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filterBy, setFilterBy] = useState<{
    metricTypes: MovementMetricType[];
    significance: ('MINOR' | 'MODERATE' | 'SIGNIFICANT')[];
    direction: ('IMPROVING' | 'DECLINING' | 'FLUCTUATING' | 'UNKNOWN')[];
  }>({
    metricTypes: [],
    significance: [],
    direction: []
  });
  const [showFilters, setShowFilters] = useState<boolean>(false);
  
  // Generate initial data and detect changes
  useEffect(() => {
    // Simulate loading delay for demo purposes
    const timer = setTimeout(() => {
      const data = generateMovementData(
        new Date(Date.now() - 90 * 24 * 60 * 60 * 1000), // 90 days ago
        new Date(),
        4,  // 4 data points per day
        true // Include subtle changes
      );
      setMovementData(data);
      setLoading(false);
      
      // Run initial analysis
      const changes = detectSubtleChanges(data, metrics, analysisConfig);
      setDetectedChanges(changes);
    }, 1500);
    
    return () => clearTimeout(timer);
  }, []);
  
  // Run analysis when config changes
  const runAnalysis = () => {
    setLoading(true);
    
    // Simulate analysis delay
    setTimeout(() => {
      const changes = detectSubtleChanges(movementData, metrics, analysisConfig);
      setDetectedChanges(changes);
      setLoading(false);
    }, 1000);
  };
  
  // Handle changes in configuration
  const handleSaveConfig = (config: AnalysisConfiguration) => {
    setAnalysisConfig(config);
    // Simulated effect of configuration change
    runAnalysis();
  };
  
  // Handle changes in notification settings
  const handleSaveNotificationSettings = (settings: SubtleChangeNotificationSettings) => {
    setNotificationSettings(settings);
    // In a real app, this might update server settings or local storage
  };
  
  // Handle reviewing a change
  const handleReviewChange = (changeId: string, isReviewed: boolean) => {
    setDetectedChanges(prev => 
      prev.map(change => 
        change.id === changeId ? { ...change, isReviewed } : change
      )
    );
  };
  
  // Handle adding change to care plan
  const handleAddToCareplan = (changeId: string, isAddressed: boolean) => {
    setDetectedChanges(prev => 
      prev.map(change => 
        change.id === changeId ? { ...change, isAddressedInCareplan: isAddressed } : change
      )
    );
  };
  
  // Filter changes based on search and filters
  const filteredChanges = detectedChanges.filter(change => {
    // Search filter
    if (searchTerm && !change.metricName.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !change.description.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    
    // Metric type filter
    if (filterBy.metricTypes.length > 0 && !filterBy.metricTypes.includes(change.metricType)) {
      return false;
    }
    
    // Significance filter
    if (filterBy.significance.length > 0 && !filterBy.significance.includes(change.significance)) {
      return false;
    }
    
    // Direction filter
    if (filterBy.direction.length > 0 && !filterBy.direction.includes(change.direction)) {
      return false;
    }
    
    return true;
  });
  
  // Toggle a metric type filter
  const toggleMetricTypeFilter = (metricType: MovementMetricType) => {
    setFilterBy(prev => {
      const types = [...prev.metricTypes];
      if (types.includes(metricType)) {
        return { ...prev, metricTypes: types.filter(t => t !== metricType) };
      } else {
        return { ...prev, metricTypes: [...types, metricType] };
      }
    });
  };
  
  // Toggle a significance filter
  const toggleSignificanceFilter = (significance: 'MINOR' | 'MODERATE' | 'SIGNIFICANT') => {
    setFilterBy(prev => {
      const significances = [...prev.significance];
      if (significances.includes(significance)) {
        return { ...prev, significance: significances.filter(s => s !== significance) };
      } else {
        return { ...prev, significance: [...significances, significance] };
      }
    });
  };
  
  // Toggle a direction filter
  const toggleDirectionFilter = (direction: 'IMPROVING' | 'DECLINING' | 'FLUCTUATING' | 'UNKNOWN') => {
    setFilterBy(prev => {
      const directions = [...prev.direction];
      if (directions.includes(direction)) {
        return { ...prev, direction: directions.filter(d => d !== direction) };
      } else {
        return { ...prev, direction: [...directions, direction] };
      }
    });
  };
  
  // Clear all filters
  const clearFilters = () => {
    setFilterBy({
      metricTypes: [],
      significance: [],
      direction: []
    });
    setSearchTerm('');
  };
  
  // Get summary counts
  const summaryData = {
    total: detectedChanges.length,
    improving: detectedChanges.filter(c => c.direction === 'IMPROVING').length,
    declining: detectedChanges.filter(c => c.direction === 'DECLINING').length,
    significant: detectedChanges.filter(c => c.significance === 'SIGNIFICANT').length,
    reviewed: detectedChanges.filter(c => c.isReviewed).length,
    inCareplan: detectedChanges.filter(c => c.isAddressedInCareplan).length
  };
  
  // Prepare trend visualization data
  const getAggregatedMetricTrend = (metricId: string) => {
    if (movementData.length === 0) return null;
    
    const startDate = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000); // 90 days ago
    const endDate = new Date();
    
    return calculateTrend(movementData, metricId, startDate, endDate);
  };
  
  // Render content based on active tab
  const renderTabContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center p-12">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          <p className="ml-3 text-gray-600">Analyzing movement data...</p>
        </div>
      );
    }
    
    switch (activeTab) {
      case 'detected':
        return (
          <div>
            {/* Filters and search */}
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-4">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4">
                <div className="relative w-full md:w-auto mb-3 md:mb-0">
                  <input
                    type="text"
                    placeholder="Search changes..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-full md:w-64"
                  />
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                </div>
                
                <div className="flex space-x-2">
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="flex items-center px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700"
                  >
                    <Filter size={18} className="mr-1" />
                    Filter
                    <ChevronDown size={16} className="ml-1" />
                  </button>
                  
                  {(filterBy.metricTypes.length > 0 || filterBy.significance.length > 0 || filterBy.direction.length > 0) && (
                    <button
                      onClick={clearFilters}
                      className="flex items-center px-3 py-2 bg-red-100 hover:bg-red-200 rounded-lg text-red-700"
                    >
                      Clear Filters
                    </button>
                  )}
                  
                  <button
                    onClick={runAnalysis}
                    className="flex items-center px-3 py-2 bg-blue-100 hover:bg-blue-200 rounded-lg text-blue-700"
                  >
                    <RefreshCw size={18} className="mr-1" />
                    Refresh
                  </button>
                </div>
              </div>
              
              {/* Filter options */}
              {showFilters && (
                <div className="mt-4 border-t pt-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Metric Type Filters */}
                    <div>
                      <h4 className="text-sm font-medium mb-2">Metric Type</h4>
                      <div className="space-y-1">
                        {Object.keys(analysisConfig.detectionThresholds).map((type) => (
                          <label key={type} className="flex items-center">
                            <input
                              type="checkbox"
                              checked={filterBy.metricTypes.includes(type as MovementMetricType)}
                              onChange={() => toggleMetricTypeFilter(type as MovementMetricType)}
                              className="mr-2 h-4 w-4 text-blue-600"
                            />
                            <span className="text-sm">{type.replace('_', ' ')}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    
                    {/* Significance Filters */}
                    <div>
                      <h4 className="text-sm font-medium mb-2">Significance</h4>
                      <div className="space-y-1">
                        {['MINOR', 'MODERATE', 'SIGNIFICANT'].map((significance) => (
                          <label key={significance} className="flex items-center">
                            <input
                              type="checkbox"
                              checked={filterBy.significance.includes(significance as 'MINOR' | 'MODERATE' | 'SIGNIFICANT')}
                              onChange={() => toggleSignificanceFilter(significance as 'MINOR' | 'MODERATE' | 'SIGNIFICANT')}
                              className="mr-2 h-4 w-4 text-blue-600"
                            />
                            <span className="text-sm">{significance.toLowerCase()}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    
                    {/* Direction Filters */}
                    <div>
                      <h4 className="text-sm font-medium mb-2">Direction</h4>
                      <div className="space-y-1">
                        {['IMPROVING', 'DECLINING', 'FLUCTUATING', 'UNKNOWN'].map((direction) => (
                          <label key={direction} className="flex items-center">
                            <input
                              type="checkbox"
                              checked={filterBy.direction.includes(direction as 'IMPROVING' | 'DECLINING' | 'FLUCTUATING' | 'UNKNOWN')}
                              onChange={() => toggleDirectionFilter(direction as 'IMPROVING' | 'DECLINING' | 'FLUCTUATING' | 'UNKNOWN')}
                              className="mr-2 h-4 w-4 text-blue-600"
                            />
                            <span className="text-sm">{direction.toLowerCase()}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Changes List */}
            <div>
              {filteredChanges.length > 0 ? (
                filteredChanges.map(change => (
                  <ChangeDetailCard
                    key={change.id}
                    change={change}
                    allChanges={detectedChanges}
                    movementData={movementData}
                    metrics={metrics}
                    onReviewChange={handleReviewChange}
                    onAddToCareplan={handleAddToCareplan}
                    expanded={detectedChanges.length === 1}
                  />
                ))
              ) : (
                <div className="bg-gray-50 rounded-lg p-8 text-center border border-gray-200">
                  <p className="text-gray-500">No changes found matching your filters.</p>
                  {detectedChanges.length > 0 && (
                    <button
                      onClick={clearFilters}
                      className="mt-2 px-4 py-2 bg-blue-100 hover:bg-blue-200 rounded-lg text-blue-700"
                    >
                      Clear Filters
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        );
        
      case 'trends':
        return (
          <div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
              <h3 className="text-lg font-medium mb-4">Movement Metrics Overview</h3>
              <p className="text-sm text-gray-600 mb-4">
                These graphs show the trend of various movement metrics over time. Subtle changes may not be immediately visible to human observers but are detected by our AI analysis.
              </p>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {metrics.slice(0, 4).map(metric => {
                  const trend = getAggregatedMetricTrend(metric.id);
                  const relatedChange = detectedChanges.find(c => c.metricId === metric.id);
                  
                  if (!trend) return null;
                  
                  return (
                    <div key={metric.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="flex justify-between items-center mb-2">
                        <h4 className="font-medium">{metric.name}</h4>
                        {relatedChange && (
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            relatedChange.direction === 'IMPROVING' ? 'bg-green-100 text-green-800' :
                            relatedChange.direction === 'DECLINING' ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {relatedChange.direction === 'IMPROVING' ? (
                              <span className="flex items-center">
                                <TrendingUp size={12} className="mr-1" /> 
                                {Math.abs(relatedChange.percentageChange).toFixed(1)}% improvement
                              </span>
                            ) : relatedChange.direction === 'DECLINING' ? (
                              <span className="flex items-center">
                                <TrendingDown size={12} className="mr-1" /> 
                                {Math.abs(relatedChange.percentageChange).toFixed(1)}% decline
                              </span>
                            ) : (
                              <span>No significant change</span>
                            )}
                          </span>
                        )}
                      </div>
                      
                      <p className="text-xs text-gray-600 mb-2">{metric.description}</p>
                      
                      <TrendVisualizer 
                        trend={trend} 
                        metric={metric} 
                        change={relatedChange}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
        
      case 'settings':
        return (
          <SettingsPanel
            config={analysisConfig}
            notificationSettings={notificationSettings}
            onSaveConfig={handleSaveConfig}
            onSaveNotificationSettings={handleSaveNotificationSettings}
            onRunAnalysis={runAnalysis}
          />
        );
        
      default:
        return null;
    }
  };
  
  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold">Subtle Change Notification System</h2>
        <p className="text-gray-600">
          AI-powered detection of gradual changes in movement patterns that may indicate emerging health concerns.
        </p>
      </div>
      
      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-500">Total Changes</h3>
            <Zap className="text-blue-500" size={20} />
          </div>
          <p className="text-2xl font-bold mt-2">{summaryData.total}</p>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-500">Improving</h3>
            <TrendingUp className="text-green-500" size={20} />
          </div>
          <p className="text-2xl font-bold mt-2">{summaryData.improving}</p>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-500">Declining</h3>
            <TrendingDown className="text-red-500" size={20} />
          </div>
          <p className="text-2xl font-bold mt-2">{summaryData.declining}</p>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-500">Significant</h3>
            <AlertIcon className="text-orange-500" size={20} />
          </div>
          <p className="text-2xl font-bold mt-2">{summaryData.significant}</p>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-500">Reviewed</h3>
            <Clock className="text-purple-500" size={20} />
          </div>
          <p className="text-2xl font-bold mt-2">{summaryData.reviewed}</p>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-500">In Care Plan</h3>
            <Settings className="text-indigo-500" size={20} />
          </div>
          <p className="text-2xl font-bold mt-2">{summaryData.inCareplan}</p>
        </div>
      </div>
      
      {/* Tabs */}
      <div className="flex border-b mb-6">
        <button
          onClick={() => setActiveTab('detected')}
          className={`px-4 py-2 font-medium ${
            activeTab === 'detected'
              ? 'border-b-2 border-blue-500 text-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Zap className="inline-block mr-2 w-5 h-5" />
          Detected Changes
        </button>
        <button
          onClick={() => setActiveTab('trends')}
          className={`px-4 py-2 font-medium ${
            activeTab === 'trends'
              ? 'border-b-2 border-blue-500 text-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <TrendingUp className="inline-block mr-2 w-5 h-5" />
          Trend Analysis
        </button>
        <button
          onClick={() => setActiveTab('settings')}
          className={`px-4 py-2 font-medium ${
            activeTab === 'settings'
              ? 'border-b-2 border-blue-500 text-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Sliders className="inline-block mr-2 w-5 h-5" />
          Settings
        </button>
      </div>
      
      {/* Content based on active tab */}
      {renderTabContent()}
    </div>
  );
};

// Helper component for the AlertIcon since it's missing from the direct imports
const AlertIcon: React.FC<{ className?: string; size?: number }> = ({ className, size }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width={size || 24} 
    height={size || 24} 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className={className}
  >
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
    <line x1="12" y1="9" x2="12" y2="13"></line>
    <line x1="12" y1="17" x2="12.01" y2="17"></line>
  </svg>
);

export default SubtleChangeDetection; 